/** @jsxImportSource react */
import React from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from '../components/ui/Toast';
import { useTranslation } from '../i18n'
import { useAuth } from '../contexts/auth'
import { UserRole } from '../types'
import { api } from '../services/api'
import {
  Download,
  Calendar,
  DollarSign,
  TrendingDown,
  TrendingUp,
  AlertCircle,
  Info,
  BarChart3,
} from 'lucide-react'

export const AccountantReportPage = () => {
  const { t, formatCurrency } = useTranslation()
  const { user } = useAuth()
  const [startDate, setStartDate] = React.useState('')
  const [endDate, setEndDate] = React.useState('')

  const { data: orders = [], isLoading: isLoadingOrders } = useQuery({
    queryKey: ['allOrdersForReport'],
    queryFn: () => api.getOrders(),
    enabled: !!user && user.role === UserRole.Admin,
  })
  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['allUsersForReport'],
    queryFn: () => api.getUsers(),
    enabled: !!user && user.role === UserRole.Admin,
  })

  const financialData = React.useMemo(() => {
    const filtered = orders.filter((order) => {
      // Only include paid orders with financial data for accurate reporting
      if (!order.finalPrice) {
        return false
      }
      if (!startDate || !endDate) return true // Show all if no date range
      const orderDate = new Date(order.createdAt)
      const endOfDay = new Date(endDate)
      endOfDay.setHours(23, 59, 59, 999)
      return orderDate >= new Date(startDate) && orderDate <= endOfDay
    })
    const kpis = filtered.reduce(
      (acc, order) => {
        const revenue = order.finalPrice || 0
        const profit = order.commission || 0
        acc.totalRevenue += revenue
        acc.totalProfit += profit
        acc.totalCost += revenue - profit
        return acc
      },
      {
        totalRevenue: 0,
        totalCost: 0,
        totalProfit: 0,
        profitMargin: 0,
      },
    )
    // Calculate profit margin
    kpis.profitMargin =
      kpis.totalRevenue > 0 ? (kpis.totalProfit / kpis.totalRevenue) * 100 : 0
    
    // Prepare detailed order data for CSV download
    const ordersWithCalculations = filtered
      .map((order) => {
        const revenue = order.finalPrice || 0
        const profit = order.commission || 0
        const cost = revenue - profit
        const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0
        return {
          ...order,
          revenue,
          cost,
          profit,
          profitMargin,
          clientName:
            users.find((u) => u.id === order.clientId)?.name || order.clientId,
        }
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    return {
      kpis,
      detailedOrders: ordersWithCalculations,
    }
  }, [orders, users, startDate, endDate])
  
  const handleDownloadCSV = () => {
    if (!startDate || !endDate) {
      toast.error(t('selectDateRangeError'))
      return
    }
    if (financialData.detailedOrders.length === 0) {
      toast.error(
        t('noOrdersInRangeError')
      )
      return
    }
    const headers = [
      t('orderId'),
      t('date'),
      t('client'),
      t('product'),
      t('revenue'),
      t('cost'),
      t('profit'),
      t('profitMargin') + ' (%)',
    ]
    const formatValue = (value: any) => {
      if (value === null || value === undefined) return ''
      const strValue = String(value)
      if (/[",\n]/.test(strValue)) {
        return `"${strValue.replace(/"/g, '""')}"`
      }
      return strValue
    }
    const csvContent = [
      headers.join(','),
      ...financialData.detailedOrders.map((order) =>
        [
          formatValue(order.orderId),
          formatValue(new Date(order.createdAt).toISOString().split('T')[0]),
          formatValue(order.clientName),
          formatValue(order.productName),
          formatValue(order.revenue.toFixed(2)),
          formatValue(order.cost.toFixed(2)),
          formatValue(order.profit.toFixed(2)),
          formatValue(order.profitMargin.toFixed(2)),
        ].join(','),
      ),
    ].join('\n')
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `accountant-report-${startDate}-to-${endDate}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
    toast.success(
      t('reportDownloadedSuccess')
    )
  }

  if (user?.role !== UserRole.Admin) {
    return <Navigate to="/dashboard" />
  }
  const isLoading = isLoadingOrders || isLoadingUsers
  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-secondary-900 mb-2">
            {t('accountantReport')}
          </h1>
          <p className="text-secondary-600 max-w-3xl">
            {t('accountantReportDesc')}
          </p>
        </div>
        {/* Date Range Selector */}
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-secondary-900">
              {t('selectDateRange')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label
                htmlFor="start-date"
                className="block text-sm font-medium text-secondary-700 mb-1"
              >
                {t('startDate')}
              </label>
              <input
                type="date"
                id="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                aria-label={t('startDate')}
              />
            </div>
            <div>
              <label
                htmlFor="end-date"
                className="block text-sm font-medium text-secondary-700 mb-1"
              >
                {t('endDate')}
              </label>
              <input
                type="date"
                id="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                aria-label={t('endDate')}
              />
            </div>
            <div>
              <button
                onClick={handleDownloadCSV}
                disabled={isLoading || !startDate || !endDate}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-600 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70 disabled:cursor-not-allowed shadow-sm"
              >
                <Download className="w-4 h-4" />
                {isLoading
                  ? t('loadingData')
                  : t('downloadCsv')}
              </button>
            </div>
          </div>
          {!startDate || !endDate ? (
            <div className="mt-4 flex items-center gap-2 text-secondary-600 bg-secondary-50 p-3 rounded-lg border border-secondary-200">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">
                {t('selectDateRangePrompt')}
              </p>
            </div>
          ) : financialData.detailedOrders.length === 0 ? (
            <div className="mt-4 flex items-center gap-2 text-secondary-600 bg-secondary-50 p-3 rounded-lg border border-secondary-200">
              <Info className="w-5 h-5" />
              <p className="text-sm">
                {t('noOrdersInRange')}
              </p>
            </div>
          ) : null}
        </div>
        {/* Financial Summary Cards */}
        {!isLoading && financialData.detailedOrders.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-secondary-500">
                    {t('totalRevenue')}
                  </p>
                  <p className="text-xl font-bold text-secondary-900">
                    {formatCurrency(financialData.kpis.totalRevenue)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center text-secondary-600">
                  <TrendingDown className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-secondary-500">
                    {t('totalCost')}
                  </p>
                  <p className="text-xl font-bold text-secondary-900">
                    {formatCurrency(financialData.kpis.totalCost)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-secondary-500">
                    {t('totalProfit')}
                  </p>
                  <p className="text-xl font-bold text-secondary-900">
                    {formatCurrency(financialData.kpis.totalProfit)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-secondary-500">
                    {t('profitMargin')}
                  </p>
                  <p className="text-xl font-bold text-secondary-900">
                    {financialData.kpis.profitMargin.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}